import type { DecisionAgentModuleCapabilityProfile } from './decision-agent-platform';

export const OBJECTIVE_TO_LEVER_DECOMPOSER_SCHEMA_VERSION =
  'objective_to_lever_decomposer_contract_v1' as const;

export type ObjectiveToLeverDecomposerSchemaVersion =
  typeof OBJECTIVE_TO_LEVER_DECOMPOSER_SCHEMA_VERSION;

export type ObjectiveQuestionFamilyId =
  | 'increase_ebitda'
  | 'reduce_transportation_cost'
  | 'identify_savings_levers'
  | 'improve_margin'
  | 'improve_working_capital'
  | 'out_of_scope';

export type ObjectiveMetricDirection = 'increase' | 'decrease' | 'maintain';

export type ObjectiveLeverBranch = 'revenue' | 'cogs' | 'sga' | 'ops' | 'capital';

export type ObjectiveLeverGrade = 'hard_$' | 'soft_$' | 'cash_returns';

export type ObjectiveRiskLevel = 'low' | 'medium' | 'high' | 'very_high';

export type ObjectiveLeverControllability =
  | 'self_help'
  | 'cycle_sensitive'
  | 'mixed'
  | 'cash_returns';

export type ObjectiveApplicabilityOperator =
  | 'exists'
  | 'missing'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains';

export type ObjectiveApplicabilityPredicate = Readonly<{
  factKey: string;
  operator: ObjectiveApplicabilityOperator;
  value?: string | number | boolean;
}>;

export type LeverFamily = Readonly<{
  id: string;
  label: string;
  branch: ObjectiveLeverBranch;
  appliesWhen: readonly ObjectiveApplicabilityPredicate[];
  hypothesisHint: string;
  evidenceNeeds: readonly string[];
  grade: ObjectiveLeverGrade;
  riskProfile: Readonly<{
    executionRisk: ObjectiveRiskLevel;
    revenueCustomerRisk: ObjectiveRiskLevel;
  }>;
  valueAttribution?: Readonly<{
    controllability: ObjectiveLeverControllability;
    cycleExposure: ObjectiveRiskLevel;
    managementActionability: ObjectiveRiskLevel;
    rationale: string;
  }>;
}>;

export function isObjectiveLeverEbitdaRollupEligible(
  lever: Pick<LeverFamily, 'branch' | 'grade'>,
): boolean {
  return lever.branch !== 'capital' && lever.grade !== 'cash_returns';
}

export type ObjectiveTaxonomyOverlayKind =
  | 'core'
  | 'industry'
  | 'tenant';

export type ObjectiveTaxonomyOverlay = Readonly<{
  overlayId: string;
  version: string;
  kind: ObjectiveTaxonomyOverlayKind;
  provenance: string;
  approvedBy: string;
  leverFamilies: readonly LeverFamily[];
}>;

export type ObjectiveModuleBindingOverlay = Readonly<{
  overlayId: string;
  version: string;
  provenance: string;
  approvedBy: string;
  bindings: readonly LeverModuleBinding[];
}>;

export type ObjectiveTaxonomyStack = Readonly<{
  stackId: string;
  taxonomyVersion: string;
  coreTaxonomyVersion: string;
  industryOverlayVersions: readonly string[];
  tenantOverlayVersion?: string;
  taxonomyOverlays: readonly string[];
  moduleBindingOverlays: readonly string[];
  resolvedLeverFamilies: readonly LeverFamily[];
  resolvedAt: string;
  approvedBy: string;
}>;

export type ObjectiveEvidenceSourceClass =
  | 'observed'
  | 'connector-derived'
  | 'public-filing'
  | 'uploaded'
  | 'manual-admin'
  | 'public'
  | 'modeled'
  | 'synthetic-demo';

export type ObjectiveProvableEvidenceSourceClass = ObjectiveEvidenceSourceClass;

export type ObjectiveFreshnessCadence =
  | 'realtime'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'ad_hoc';

export type MissingStateUX = 'connect_X' | 'upload' | 'confirm' | 'not_actionable';

export type EvidenceReadinessStatus = 'ready' | 'missing' | 'stale' | 'wrong_class';

export type EvidenceRequirement = Readonly<{
  evidenceId: string;
  description: string;
  sourceClass: ObjectiveEvidenceSourceClass;
  requiredFreshness: ObjectiveFreshnessCadence;
  requiredAsOfWindow: string;
  joinKey: string;
}>;

export type ProofPolicyEvidenceRequirement = Readonly<{
  evidenceId: string;
  sourceClass: ObjectiveEvidenceSourceClass;
  requiredFreshness: ObjectiveFreshnessCadence;
}>;

export type SourceBinding = Readonly<{
  evidenceId: string;
  sourceSystem: string;
  governedSnapshotId: string;
  owner: string;
  asOf: string;
  sourceClass: ObjectiveEvidenceSourceClass;
  freshness: ObjectiveFreshnessCadence;
  sourceObjectId?: string;
  reconciliationRule?: string;
}>;

export type EvidenceReadinessResult =
  | Readonly<{
      evidenceId: string;
      present: true;
      sourceClass: ObjectiveProvableEvidenceSourceClass;
      freshnessOk: true;
      asOf: string;
      status: 'ready';
      missingStateUX: 'not_actionable';
      sourceBindingRef: string;
    }>
  | Readonly<{
      evidenceId: string;
      present: false;
      sourceClass: ObjectiveEvidenceSourceClass;
      freshnessOk: false;
      asOf: null;
      status: 'missing';
      missingStateUX: MissingStateUX;
      sourceBindingRef?: string;
    }>
  | Readonly<{
      evidenceId: string;
      present: true;
      sourceClass: ObjectiveProvableEvidenceSourceClass;
      freshnessOk: false;
      asOf: string;
      status: 'stale';
      missingStateUX: MissingStateUX;
      sourceBindingRef: string;
    }>
  | Readonly<{
      evidenceId: string;
      present: true;
      sourceClass: 'synthetic-demo';
      freshnessOk: false;
      asOf: string | null;
      status: 'wrong_class';
      missingStateUX: MissingStateUX;
      sourceBindingRef?: string;
    }>;

export type ObjectiveRuntimePurpose =
  | 'read_state'
  | 'evaluate_monitoring'
  | 'run_scenarios'
  | 'build_decision_pack'
  | 'approve_activate';

export type GovernedOutput = Readonly<{
  metricKey: string;
  label: string;
  unit: string;
  definition: string;
}>;

export type ProofRequirement = Readonly<{
  proofKey: string;
  description: string;
}>;

export type PermissionRequirement = Readonly<{
  permission: string;
  required: boolean;
}>;

export type ObjectiveActivationPolicy =
  | 'preview_only'
  | 'approval_required'
  | 'not_actionable';

// Per-lever contribution contract: governs which of a module's numeric claim ids
// the composite EBITDA roll-up will ACCEPT as this lever's governed contribution,
// and which generic / un-receipted claim ids it must REJECT (anti-fabrication).
// Optional: bindings without it impose no contract-level claim-id constraints, so
// existing bindings are unaffected. Domain knowledge that used to be hardcoded in
// the aggregator (which module owns the lever, and which generic claim id is a
// non-receipted org number) now lives here in governed config instead of code.
// Overlap-family override for a specific accepted contribution claim id: the
// claim's dollars belong to a DIFFERENT economic family than the binding's
// lever (e.g. the non-workforce org-cost initiative pool riding the delayering
// binding). Overlap/disjointness resolution for a matching claim MUST use the
// override family's lever-overlap-registry declarations — never the carrying
// lever's. Without this, a non-labor dollar claim inherits the labor cluster's
// structural-disjoint declarations on a vacuous basis and can ride into a
// certified combined figure with no receipt and no member proof.
export type LeverContributionOverlapFamilyOverride = Readonly<{
  // Claim-id substring the override applies to.
  claimIdPattern: string;
  // The lever-overlap-registry family whose declarations govern the claim.
  overlapFamilyId: string;
}>;

export type LeverContributionContract = Readonly<{
  // Generic / non-receipted contribution claim-id substrings that, when emitted by
  // a supported module for this lever, must NOT be counted as a governed
  // contribution (e.g. a generic org number that is not a receipted sub-lever).
  rejectedGenericContributionClaimIdPatterns?: readonly string[];
  // Claim-id substrings that ARE this lever's valid, receipted governed
  // contribution (used for receipt-line / contribution-presence detection).
  acceptedContributionClaimIdPatterns?: readonly string[];
  // Canonical claim IDs that must match exactly when this lever requires one
  // fixed formula-runtime contribution key.
  acceptedExactContributionClaimIds?: readonly string[];
  // Claim-class overlap-family overrides (vacuous-basis guard; see above).
  contributionOverlapFamilyOverrides?: readonly LeverContributionOverlapFamilyOverride[];
}>;

export type LeverModuleBinding = Readonly<{
  bindingId: string;
  leverFamilyId: string;
  supportedQuestionFamilies: readonly ObjectiveQuestionFamilyId[];
  supportedModuleIds: readonly string[];
  requiredEvidence: readonly EvidenceRequirement[];
  supportedToolPurposes: readonly ObjectiveRuntimePurpose[];
  outputMetrics: readonly GovernedOutput[];
  proofRequirements: readonly ProofRequirement[];
  permissions: readonly PermissionRequirement[];
  activationPolicy: ObjectiveActivationPolicy;
  moduleCatalogVersion: string;
  contributionContract?: LeverContributionContract;
}>;

export type ObjectiveModuleStatus =
  | 'withheld'
  | 'shadow'
  | 'governed'
  | 'active'
  | 'retired';

export type ModuleCatalogEntry = Readonly<{
  moduleId: string;
  moduleCatalogVersion: string;
  status: ObjectiveModuleStatus;
  capabilityProfile: DecisionAgentModuleCapabilityProfile;
  decisionFamilies: readonly string[];
  governedTools: readonly Readonly<{
    toolName: string;
    runtimePurpose: ObjectiveRuntimePurpose;
    sideEffectClass: string;
    approvalPolicy: string;
    requiredPermissions: readonly string[];
  }>[];
  requiredStateKeys: readonly string[];
  proofPolicyRef: string;
  proofPolicyRequiredEvidenceRefs: readonly string[];
  proofPolicyRequiredEvidence: readonly ProofPolicyEvidenceRequirement[];
  manifestVersion?: string;
  sourceManifestRef: string;
}>;

export type TenantBusinessProfile = Readonly<{
  tenantId: string;
  profileVersion: string;
  companyName: string;
  industry: string;
  businessModel: string;
  revenueScale?: Readonly<{
    value: number;
    currency: string;
    evidenceRef: string;
  }>;
  geography: readonly string[];
  operatingFootprint: Readonly<
    Record<
      string,
      | string
      | number
      | boolean
      | readonly string[]
      | Readonly<{ count?: number; evidenceRef?: string }>
      | undefined
    >
  >;
  financeBaselineRefs: readonly string[];
  orgSnapshotRefs: readonly string[];
  logisticsSnapshotRefs: readonly string[];
  connectedSystems: readonly Readonly<{
    systemId: string;
    domain: string;
    status: 'connected' | 'missing' | 'stale' | 'unknown';
  }>[];
  knownConstraints: readonly Readonly<{
    constraintId: string;
    label: string;
    evidenceRef: string;
  }>[];
  dataGaps: readonly Readonly<{
    gapId: string;
    label: string;
    missingStateUX: MissingStateUX;
  }>[];
  evidenceRefs: readonly string[];
  asOf: string;
}>;

// Distinguishes the approved-plan objective frame from an asker-stated frame
// whose target/gap are derived by deterministic arithmetic from the approved
// baseline plus one asker-stated parameter. Absent => approved plan (default).
export type ObjectiveFrameKind = 'approved_plan' | 'asker_stated_derived';

// The asker-stated parameter that re-parameterized the objective frame. Quoted
// input, not evidence: the derivation is deterministic arithmetic over the
// governed baseline. Carried so narration/canvas can state the asker-stated
// target alongside the approved plan once, and never render it as approved.
export type AskerStatedObjectiveParameter = Readonly<{
  kind: 'percent' | 'absolute_usd';
  value: number;
  label: string;
}>;

export type ObjectiveValueGapAnchor =
  | Readonly<{
      kind: 'evidence_backed';
      metricDefinitionId: string;
      objectiveMetricId?: string;
      baselineMetricKey?: string;
      targetMetricKey?: string;
      gapMetricKey?: string;
      baselineEvidenceRef: string;
      baselineValue: number;
      targetDelta: number;
      targetValue: number;
      gapPct?: number;
      formulaId?: string;
      formulaVersion?: string;
      evidenceRefs?: readonly string[];
      sourceClass: ObjectiveProvableEvidenceSourceClass;
      sourceBindingRef: string;
      asOf: string;
      blockedIfMissing: false;
      // Asker-stated re-parameterization (all optional; approved plan omits them).
      objectiveFrameKind?: ObjectiveFrameKind;
      statedParameter?: AskerStatedObjectiveParameter;
      // The approved-plan values, retained so answers state the asker-stated
      // target vs the approved plan once. Present only on a derived frame.
      approvedTargetValue?: number;
      approvedTargetDelta?: number;
      approvedGapPct?: number;
    }>
  | Readonly<{
      kind: 'blocked';
      metricDefinitionId: string;
      objectiveMetricId?: string;
      baselineEvidenceRef: null;
      baselineValue: null;
      targetDelta: null;
      targetValue: null;
      blockedReason: string;
      missingStateUX: MissingStateUX;
      blockedIfMissing: true;
    }>;

export type ObjectiveFrame = Readonly<{
  schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
  objective: Readonly<{
    metric: string;
    direction: ObjectiveMetricDirection;
    targetPct?: number;
    scope: string;
    timeframe: string;
  }>;
  valueGapAnchor: ObjectiveValueGapAnchor;
  meta: Readonly<{
    promptPackVersion: string;
    schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
    taxonomyVersion: string;
    taxonomyStack: readonly string[];
    moduleCatalogVersion: string;
  }>;
}>;

export type ObjectiveContextPack = Readonly<{
  contextPackId: string;
  schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
  objectiveText: string;
  profile: TenantBusinessProfile;
  objectiveFrame: ObjectiveFrame;
  relevantFacts: readonly Readonly<{
    factKey: string;
    value: string | number | boolean | null;
    unit?: string;
    evidenceRef: string;
    asOf: string;
  }>[];
  knownDataGaps: TenantBusinessProfile['dataGaps'];
  taxonomyStack: ObjectiveTaxonomyStack;
  moduleCatalogVersion: string;
  promptPackVersion: string;
  evidenceRefs: readonly string[];
  redactionPolicy: 'STRICT_CLEAN_ROOM' | 'ALLOW_PII';
}>;

export type ObjectiveLeverStatusHint =
  | 'may_be_applicable'
  | 'needs_evidence'
  | 'not_applicable'
  | 'candidate_for_review';

export type ObjectiveResolvedLeverStatus =
  | 'routable'
  | 'needs_evidence'
  | 'module_missing'
  | 'not_applicable'
  | 'candidate_for_review';

export type ObjectiveRoutingGate =
  | Readonly<{
      gateStatus: 'routable';
      resultStatus: 'routable';
      evaluatedAt: string;
      moduleStatus: 'governed' | 'active';
      permissionsOk: true;
      evidenceReady: true;
      sourceClassOk: true;
      freshnessOk: true;
      proofPolicyOk: true;
      activationPolicy: 'approval_required' | 'read_only';
      activationAllowedFromDecomposer: false;
      blockedReasons: readonly [];
    }>
  | Readonly<{
      gateStatus: 'blocked';
      resultStatus: Exclude<ObjectiveResolvedLeverStatus, 'routable'>;
      evaluatedAt: string;
      moduleStatus: ObjectiveModuleStatus | 'not_registered';
      permissionsOk: boolean;
      evidenceReady: boolean;
      sourceClassOk: boolean;
      freshnessOk: boolean;
      proofPolicyOk: boolean;
      activationPolicy: 'approval_required' | 'read_only' | 'not_available';
      activationAllowedFromDecomposer: false;
      blockedReasons: readonly string[];
    }>;

export type ObjectiveConfidenceQualitative = 'low' | 'medium' | 'high';

export type LeverPlan = Readonly<{
  schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
  taxonomyVersion: string;
  questionFamily: ObjectiveQuestionFamilyId;
  factsUsed: readonly string[];
  levers: readonly Readonly<{
    leverFamilyId: string;
    rationale: string;
    evidenceNeeds: readonly string[];
    statusHint: ObjectiveLeverStatusHint;
    confidenceQualitative: ObjectiveConfidenceQualitative;
  }>[];
  excludedLevers: readonly Readonly<{
    leverFamilyId: string;
    reason: string;
  }>[];
  unaddressed: readonly Readonly<{
    area: string;
    reason: string;
    connect: string;
  }>[];
}>;

export type ResolvedLeverPlan = Readonly<{
  schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
  objectiveFrame: ObjectiveFrame;
  leverPlan: LeverPlan;
  resolved: readonly Readonly<{
    leverFamilyId: string;
    resolvedModules: readonly Readonly<{
      moduleId: string;
      toolName: string;
      runtimePurpose: ObjectiveRuntimePurpose;
      status: ObjectiveModuleStatus;
      evidenceReadinessRef: string;
      routingGate: ObjectiveRoutingGate;
    }>[];
    statusSource: 'deterministic_routing_gate';
    status: ObjectiveResolvedLeverStatus;
  }>[];
}>;

export type ObjectiveDecomposerPromptRolloutStatus =
  | 'draft'
  | 'shadow'
  | 'active'
  | 'retired';

export type ObjectiveDecomposerPromptPack = Readonly<{
  promptPackId: string;
  promptPackVersion: string;
  schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
  taxonomyVersion: string;
  moduleCatalogVersion: string;
  systemInstructionsRef: string;
  outputSchemaRef: string;
  evalSuiteId: string;
  owner: string;
  approvedBy: string;
  rolloutStatus: ObjectiveDecomposerPromptRolloutStatus;
  contentHash: string;
}>;

export type ObjectiveDecomposerEvalAssertionId =
  | 'objective_parse'
  | 'value_gap_anchor_code_owned'
  | 'taxonomy_id_valid'
  | 'no_model_dollars'
  | 'no_model_module_ids'
  | 'gap_status_correct'
  | 'wrong_tool_prevention'
  | 'proof_linked_answer'
  | 'three_bucket_partition';

export type ObjectiveDecomposerEvalTrace = Readonly<{
  evalId: string;
  title: string;
  question: string;
  fixtureId: string;
  expectedQuestionFamily: ObjectiveQuestionFamilyId;
  expectedLeverFamilies: readonly string[];
  expectedStatuses: Readonly<Record<string, ObjectiveResolvedLeverStatus>>;
  assertions: readonly ObjectiveDecomposerEvalAssertionId[];
  expectedBlockReason?: string;
}>;

export type ObjectiveDecomposerFixture = Readonly<{
  fixtureId: string;
  schemaVersion: ObjectiveToLeverDecomposerSchemaVersion;
  profile: TenantBusinessProfile;
  taxonomyStack: ObjectiveTaxonomyStack;
  objectiveFrame: ObjectiveFrame;
  sourceBindings: readonly SourceBinding[];
  evidenceReadiness: readonly EvidenceReadinessResult[];
}>;

export const V1_LOGISTICS_EBITDA_TAXONOMY_STACK_VERSION =
  'taxonomy-stack:ebitda-core@1.1+transportation-logistics@1.0' as const;

// Industry-neutral SG&A / ops / revenue / capital lever families. Despite the historical
// `ebitda-core` overlayId and the logistics provenance doc, this overlay is cross-industry;
// the canonical export name is `OBJECTIVE_CORE_TAXONOMY_OVERLAY`. The
// `V1_LOGISTICS_EBITDA_CORE_TAXONOMY_OVERLAY` name is retained below as a deprecated alias.
export const OBJECTIVE_CORE_TAXONOMY_OVERLAY = {
  overlayId: 'ebitda-core',
  version: 'ebitda-core@1.3',
  kind: 'core',
  provenance:
    'docs/html/2026-05-25/v1-logistics-lever-taxonomy.html#decision',
  approvedBy: 'orchestrator-cto',
  leverFamilies: [
    {
      id: 'delayering',
      label: 'Flatten org / right-size span-of-control',
      branch: 'sga',
      appliesWhen: [
        { factKey: 'manager_count', operator: 'gt', value: 0 },
        { factKey: 'avg_span_vs_benchmark', operator: 'lt', value: 1 },
      ],
      hypothesisHint:
        'SG&A span and layer work can expand EBITDA only after governed org, compensation, and severance evidence are connected.',
      evidenceNeeds: [
        'org_state_as_is',
        'span_layer_metrics',
        'position_cost_roster',
        'people_tenure',
        'severance_policy',
        'initiative_capacity_binding',
      ],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'high', revenueCustomerRisk: 'low' },
    },
    {
      id: 'span_of_control_rightsizing',
      label: 'Span-of-control right-sizing',
      branch: 'sga',
      appliesWhen: [
        { factKey: 'manager_count', operator: 'gt', value: 0 },
        { factKey: 'avg_span_vs_benchmark', operator: 'lt', value: 1 },
      ],
      hypothesisHint:
        'Span-of-control right-sizing can expand EBITDA only when governed span, layer, position-cost, and initiative-capacity evidence prove the move does not double-count delayering savings.',
      evidenceNeeds: [
        'org_state_as_is',
        'span_layer_metrics',
        'position_cost_roster',
        'initiative_capacity_binding',
      ],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'workforce_reduction',
      label: 'Workforce reduction scenario',
      branch: 'sga',
      appliesWhen: [{ factKey: 'position_cost_roster', operator: 'exists' }],
      hypothesisHint:
        'Workforce reduction can expand EBITDA only when governed cost, severance, legal opt-in, capacity, and overlap evidence prove the move is financially real and execution-safe.',
      evidenceNeeds: [
        'org_state_as_is',
        'position_cost_roster',
        'people_tenure',
        'severance_policy',
        'initiative_capacity_binding',
        'workforce_legal_gate',
      ],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'high', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'productivity_lean_ai',
      label: 'Productivity through Lean + AI',
      branch: 'ops',
      appliesWhen: [
        { factKey: 'automation_eligible_operating_cost', operator: 'exists' },
      ],
      hypothesisHint:
        'Lean + AI cost release can expand EBITDA only when governed throughput, automation-eligible cost, release-factor, and AI-investment evidence is connected; it shares labor cost pools with org levers and needs shared-scope receipts before compositing.',
      evidenceNeeds: ['approved_productivity_realization_snapshot'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'segment_margin_cleanup',
      label: 'Segment and account margin cleanup',
      branch: 'revenue',
      appliesWhen: [{ factKey: 'segment_margin_snapshot', operator: 'exists' }],
      hypothesisHint:
        'Segment margin cleanup is an allocation lens over the same P&L dollars; its contribution stays non-additive until exclusion receipts tie cleanup dollars to named underlying actions.',
      evidenceNeeds: ['approved_segment_margin_snapshot'],
      grade: 'soft_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'sga_spend',
      label: 'Optimize certified SG&A cost pools',
      branch: 'sga',
      appliesWhen: [
        { factKey: 'certified_sga_lever_metrics', operator: 'exists' },
      ],
      hypothesisHint:
        'The certified SG&A portfolio can expand operating income only through one fixed-key aggregate claim whose attributed cost-pool members replace, rather than add to, its leaf claims.',
      evidenceNeeds: [
        'certified_sga_lever_metrics',
        'finance_sga_line_reconciliation',
      ],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'indirect_procurement',
      label: 'Consolidate residual indirect spend (MRO, supplies, outsourced ops)',
      branch: 'sga',
      appliesWhen: [{ factKey: 'indirect_spend_cube', operator: 'exists' }],
      hypothesisHint:
        'Non-transport vendor consolidation can expand EBITDA when governed finance spend-cube evidence is connected.',
      evidenceNeeds: ['vendor_spend_by_category'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'low' },
    },
    {
      id: 'travel_and_expense',
      label: 'Reduce T&E leakage and policy exceptions',
      branch: 'sga',
      appliesWhen: [{ factKey: 'travel_expense_spend', operator: 'gt', value: 0 }],
      hypothesisHint:
        'Travel and expense leakage can expand EBITDA only when card, expense, and policy-exception evidence is governed.',
      evidenceNeeds: ['expense_card_spend', 'travel_policy_exceptions', 'department_cost_center'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'low' },
    },
    {
      id: 'professional_fees',
      label: 'Rationalize legal, consulting, audit, and advisory fees',
      branch: 'sga',
      appliesWhen: [{ factKey: 'professional_fee_spend', operator: 'gt', value: 0 }],
      hypothesisHint:
        'Professional fee rationalization is a CFO lever when vendor, matter, and statement-of-work evidence is connected.',
      evidenceNeeds: ['professional_fee_vendor_spend', 'statements_of_work', 'matter_or_project_codes'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'tech_software',
      label: 'Rationalize SaaS, licenses, and infrastructure',
      branch: 'sga',
      appliesWhen: [{ factKey: 'software_license_spend', operator: 'gt', value: 0 }],
      hypothesisHint:
        'Technology and software rationalization can expand EBITDA when license, utilization, renewal, and owner evidence is governed.',
      evidenceNeeds: ['software_license_inventory', 'usage_telemetry', 'renewal_calendar', 'application_owner'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'outside_contractors',
      label: 'Convert or reduce outside contractors and contingent labor',
      branch: 'sga',
      appliesWhen: [{ factKey: 'contractor_spend', operator: 'gt', value: 0 }],
      hypothesisHint:
        'Contractor and contingent labor leakage is quantifiable only with role, rate, duration, and accountable-owner evidence.',
      evidenceNeeds: ['contractor_roster', 'bill_rates', 'workforce_plan', 'sow_end_dates'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'labor_arbitrage',
      label: 'Shift eligible work to lower-cost delivery locations',
      branch: 'sga',
      appliesWhen: [{ factKey: 'location_cost_differential', operator: 'exists' }],
      hypothesisHint:
        'Labor arbitrage is an unlockable SG&A lever when role eligibility, location cost, and service-risk evidence is governed.',
      evidenceNeeds: ['role_location_costs', 'process_eligibility', 'service_level_constraints', 'transition_costs'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'high', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'real_estate_footprint',
      label: 'Rationalize real estate and facility footprint',
      branch: 'sga',
      appliesWhen: [{ factKey: 'lease_and_facility_costs', operator: 'exists' }],
      hypothesisHint:
        'Real estate footprint rationalization requires governed lease, utilization, exit cost, and operational dependency evidence.',
      evidenceNeeds: ['lease_schedule', 'facility_costs', 'utilization', 'exit_costs'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'shared_services',
      label: 'Consolidate back-office shared functions',
      branch: 'sga',
      appliesWhen: [{ factKey: 'function_cost_by_location', operator: 'exists' }],
      hypothesisHint:
        'Shared-services consolidation can expand EBITDA only with function, location, service-level, and transition evidence.',
      evidenceNeeds: ['function_headcount', 'function_cost_by_location', 'service_level_baselines', 'transition_plan'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'marketing_spend',
      label: 'Rationalize marketing and demand-generation spend',
      branch: 'sga',
      appliesWhen: [{ factKey: 'marketing_spend_cube', operator: 'exists' }],
      hypothesisHint:
        'Marketing and demand-generation consolidation (agency, media, events, martech) can expand EBITDA only with total spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
      evidenceNeeds: ['marketing_spend_cube', 'marketing_addressable_spend', 'marketing_program_cost'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'pricing_uplift',
      label: 'Spot and contract price realization',
      branch: 'revenue',
      appliesWhen: [
        { factKey: 'spot_freight_volume', operator: 'gt', value: 0 },
      ],
      hypothesisHint:
        'Price realization can expand EBITDA, but impact must stay ranged and churn-risk qualified until pricing and elasticity evidence are governed.',
      evidenceNeeds: [
        'freight_volume',
        'price_history',
        'elasticity_assumption',
        'customer_concentration',
      ],
      grade: 'soft_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'high' },
    },
    {
      id: 'discount_leakage',
      label: 'Recover quote-to-invoice rate leakage',
      branch: 'revenue',
      appliesWhen: [{ factKey: 'quote_invoice_variance', operator: 'exists' }],
      hypothesisHint:
        'Discount leakage is a soft-dollar revenue lever until quote, invoice, rate-card, and exception evidence is reconciled.',
      evidenceNeeds: ['quotes', 'invoices', 'rate_cards', 'approval_exceptions'],
      grade: 'soft_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'revenue_mix_shift',
      label: 'Shift toward higher-margin lanes and services',
      branch: 'revenue',
      appliesWhen: [{ factKey: 'margin_by_service', operator: 'exists' }],
      hypothesisHint:
        'Revenue mix shift is unlockable when lane, service, margin, and capacity evidence can prove profitable substitution.',
      evidenceNeeds: ['lane_margin', 'service_margin', 'customer_demand', 'capacity_constraints'],
      grade: 'soft_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'growth_cross_sell',
      label: 'Grow and cross-sell into existing accounts',
      branch: 'revenue',
      appliesWhen: [{ factKey: 'account_whitespace', operator: 'exists' }],
      hypothesisHint:
        'Organic growth and cross-sell upside must remain ranged until account whitespace, win-rate, and capacity evidence is governed.',
      evidenceNeeds: ['account_whitespace', 'pipeline', 'win_rate', 'capacity_to_serve'],
      grade: 'soft_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'churn_reduction',
      label: 'Improve retention and reduce churn',
      branch: 'revenue',
      appliesWhen: [{ factKey: 'customer_churn_signal', operator: 'exists' }],
      hypothesisHint:
        'Retention is a soft-dollar EBITDA lever only when churn, customer health, service quality, and margin evidence are connected.',
      evidenceNeeds: ['customer_revenue', 'churn_history', 'service_quality', 'customer_margin'],
      grade: 'soft_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'working_capital',
      label: 'Improve DSO, DPO, and inventory cash conversion',
      branch: 'capital',
      appliesWhen: [{ factKey: 'working_capital_balance', operator: 'exists' }],
      hypothesisHint:
        'Working capital is CFO-relevant and cash-accretive, but it is flagged as EBITDA-adjacent rather than an EBITDA compute lever.',
      evidenceNeeds: ['accounts_receivable_aging', 'accounts_payable_terms', 'inventory_aging', 'cash_conversion_cycle'],
      grade: 'cash_returns',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'low' },
    },
    {
      id: 'capex_discipline',
      label: 'Rationalize capex and project returns',
      branch: 'capital',
      appliesWhen: [{ factKey: 'capex_plan', operator: 'exists' }],
      hypothesisHint:
        'Capex discipline is a returns/cash lever and must stay separated from EBITDA improvement until a capital bridge is in scope.',
      evidenceNeeds: ['capex_plan', 'project_returns', 'approval_stage', 'asset_utilization'],
      grade: 'cash_returns',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'low' },
    },
  ],
} as const satisfies ObjectiveTaxonomyOverlay;

/**
 * @deprecated Use {@link OBJECTIVE_CORE_TAXONOMY_OVERLAY}. The core overlay is industry-neutral;
 * this logistics-named alias is retained only for backward compatibility during the
 * substrate-generalization migration and will be removed once all consumers move to the resolver.
 */
export const V1_LOGISTICS_EBITDA_CORE_TAXONOMY_OVERLAY = OBJECTIVE_CORE_TAXONOMY_OVERLAY;

export const V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY = {
  overlayId: 'transportation-logistics',
  version: 'transportation-logistics@1.0',
  kind: 'industry',
  provenance:
    'docs/html/2026-05-25/v1-logistics-lever-taxonomy.html#decision',
  approvedBy: 'orchestrator-cto',
  leverFamilies: [
    {
      id: 'carrier_renegotiation',
      label: 'Renegotiate purchased transportation + optimize lanes',
      branch: 'cogs',
      appliesWhen: [
        { factKey: 'purchased_transportation_spend', operator: 'gt', value: 0 },
      ],
      hypothesisHint:
        'Purchased transportation creates a hard-dollar rate, contract, and lane renegotiation lever when governed TMS evidence is connected.',
      evidenceNeeds: [
        'carrier_rate_card',
        'shipment_lane_spend',
        'carrier_contract_terms',
      ],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'network_optimization',
      label: 'Redesign network / consolidate routes and stops',
      branch: 'cogs',
      appliesWhen: [{ factKey: 'lane_route_facility_data', operator: 'exists' }],
      hypothesisHint:
        'Lane, route, facility, and service-level evidence can unlock a network design lever for freight COGS.',
      evidenceNeeds: ['lane_network', 'shipment_lane_volume', 'service_levels'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'high', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'mode_mix_shift',
      label: 'Shift TL, LTL, intermodal, and parcel mode mix',
      branch: 'cogs',
      appliesWhen: [{ factKey: 'multi_mode_shipment_data', operator: 'exists' }],
      hypothesisHint:
        'Mode mix shift can reduce COGS when shipment mode, distance, weight, service-level, and capacity evidence is connected.',
      evidenceNeeds: ['shipment_mode', 'shipment_weight_distance', 'service_levels', 'mode_capacity'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'capacity_utilization',
      label: 'Improve backhaul, trailer, and capacity utilization',
      branch: 'cogs',
      appliesWhen: [{ factKey: 'asset_or_capacity_utilization', operator: 'exists' }],
      hypothesisHint:
        'Capacity utilization is unlockable when empty miles, asset utilization, route plans, and load factors are governed.',
      evidenceNeeds: ['empty_miles', 'asset_utilization', 'route_plans', 'load_factor'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'fuel_efficiency',
      label: 'Reduce fuel and accessorial leakage',
      branch: 'cogs',
      appliesWhen: [{ factKey: 'fuel_and_accessorial_spend', operator: 'gt', value: 0 }],
      hypothesisHint:
        'Fuel and accessorial leakage can be quantified when surcharge, accessorial, route, and invoice evidence is reconciled.',
      evidenceNeeds: ['fuel_surcharges', 'accessorial_charges', 'route_distance', 'carrier_invoice_lines'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'low', revenueCustomerRisk: 'low' },
    },
    {
      id: 'risk_claims',
      label: 'Reduce cargo-claims loss and recover leakage',
      branch: 'ops',
      appliesWhen: [{ factKey: 'approved_claims_register', operator: 'exists' }],
      hypothesisHint:
        'Claims prevention and recovery can expand operating income only from the approved claims register and chart-of-accounts mapping; the contribution stays candidate-only until shared claims-expense GL accounts are de-conflicted against SG&A and the segment allocation lens.',
      evidenceNeeds: ['approved_claims_register', 'approved_chart_of_accounts_mapping'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'medium' },
    },
    {
      id: 'warehouse_productivity',
      label: 'Improve warehouse labor productivity',
      branch: 'ops',
      appliesWhen: [{ factKey: 'wms_labor_hours', operator: 'exists' }],
      hypothesisHint:
        'WMS throughput and labor-hour evidence can unlock warehouse productivity as an operations EBITDA lever.',
      evidenceNeeds: ['warehouse_labor_hours', 'throughput', 'wms_activity'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
    {
      id: 'automation',
      label: 'Automate warehouse and operating processes',
      branch: 'ops',
      appliesWhen: [{ factKey: 'manual_process_volume', operator: 'exists' }],
      hypothesisHint:
        'Automation is an operations lever only when process volume, labor effort, capex, and payback evidence is governed.',
      evidenceNeeds: ['manual_process_volume', 'labor_hours', 'automation_capex', 'payback_assumption'],
      grade: 'hard_$',
      riskProfile: { executionRisk: 'medium', revenueCustomerRisk: 'low' },
    },
  ],
} as const satisfies ObjectiveTaxonomyOverlay;

export function buildV1LogisticsEbitdaTaxonomyStack(
  input: Readonly<{
    stackId?: string;
    resolvedAt: string;
    approvedBy?: string;
    tenantOverlayVersion?: string;
  }>,
): ObjectiveTaxonomyStack {
  return {
    stackId: input.stackId ?? 'taxonomy_stack:northwind_logistics:ebitda:v1',
    taxonomyVersion: V1_LOGISTICS_EBITDA_TAXONOMY_STACK_VERSION,
    coreTaxonomyVersion: OBJECTIVE_CORE_TAXONOMY_OVERLAY.version,
    industryOverlayVersions: [
      V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY.version,
    ],
    tenantOverlayVersion: input.tenantOverlayVersion,
    taxonomyOverlays: [
      OBJECTIVE_CORE_TAXONOMY_OVERLAY.version,
      V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY.version,
    ],
    moduleBindingOverlays: ['lever-module-bindings:v1-logistics-ebitda@1.1'],
    resolvedLeverFamilies: [
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'carrier_renegotiation',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'network_optimization',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'mode_mix_shift',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'capacity_utilization',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'fuel_efficiency',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'risk_claims',
      ),
      findLeverFamily(OBJECTIVE_CORE_TAXONOMY_OVERLAY, 'delayering'),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'productivity_lean_ai',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'segment_margin_cleanup',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'span_of_control_rightsizing',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'workforce_reduction',
      ),
      findLeverFamily(OBJECTIVE_CORE_TAXONOMY_OVERLAY, 'sga_spend'),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'travel_and_expense',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'indirect_procurement',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'professional_fees',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'tech_software',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'outside_contractors',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'labor_arbitrage',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'real_estate_footprint',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'shared_services',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'pricing_uplift',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'discount_leakage',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'revenue_mix_shift',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'growth_cross_sell',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'churn_reduction',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'warehouse_productivity',
      ),
      findLeverFamily(
        V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY,
        'automation',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'working_capital',
      ),
      findLeverFamily(
        OBJECTIVE_CORE_TAXONOMY_OVERLAY,
        'capex_discipline',
      ),
    ],
    resolvedAt: input.resolvedAt,
    approvedBy: input.approvedBy ?? 'orchestrator-cto',
  };
}

// TWO LAYERS, DELIBERATELY SEPARATE — read before adding or removing anything here.
//
//   LEVER VOCABULARY  (above: OBJECTIVE_CORE_TAXONOMY_OVERLAY /
//   V1_LOGISTICS_TRANSPORTATION_TAXONOMY_OVERLAY `leverFamilies`) names a business
//   lever. A named lever with no engine is not a lie — the decomposer models it
//   honestly as `module_missing` (objective-to-lever-semantic-layer.ts
//   `summarizeResolvedStatus`: zero resolved modules → 'module_missing'), which is how
//   an unbuilt lever stays VISIBLE as a named gap instead of silently disappearing.
//
//   ENGINE BINDINGS   (below: `bindings`) are the other thing entirely. A binding's
//   `supportedModuleIds` is what makes a moduleId ADMISSION-ELIGIBLE:
//   ask-helix.bridge-roster.ts DERIVES `ASK_HELIX_BRIDGE_ELIGIBLE_MODULE_IDS` from this
//   array, so anything named here is offered to the composite-EBITDA bridge.
//
// A binding therefore asserts "an engine exists behind this moduleId". Never declare a
// binding for a module that has no code — declare the lever family and let it resolve
// to `module_missing`.
//
// UPDATED 2026-08-05 (#4449, follow-up to #4494) — five ENGINE BINDINGS removed:
//   mode-mix-shift, revenue-mix-shift, growth-cross-sell, working-capital,
//   capex-discipline.
// #4494 retired those five stub modules (trees deleted, zero tracked paths) but did not
// touch this overlay, so three of them (mode-mix-shift, revenue-mix-shift,
// growth-cross-sell) stayed admission-eligible for the EBITDA bridge with no code behind
// them — the defect #4494 filed in its own PR body rather than fixing mid-retirement.
//
// Their lever FAMILIES are deliberately KEPT in the taxonomy overlays, and so are their
// entries in lever-overlap-registry / advisory-lever-guidance: the business levers are
// real and still worth naming, they simply have no engine yet. Removing the binding is
// exactly how this file expresses that — `EBITDA_DECOMPOSER_EVAL_TRACES` E9 already
// declares all five as `module_missing`, and deleting the binding is what makes the
// runtime agree with that trace.
//
// Two alternatives were considered and rejected:
//   - `supportedModuleIds: []` (keep the binding, empty the engine list). Ends admission
//     the same way, but leaves an ORPHAN binding — a rollup-eligible non-leaf EBITDA
//     lever bound to no admitted module — which violates the governed "no orphan lever"
//     invariant in ask-helix.bridge-roster.spec.ts. That invariant is load-bearing and
//     was not weakened to make room for retired stubs.
//   - `supportedModuleIds: ['capability_validator']`, mirroring the file overlay at
//     apps/api/config/.../module-bindings/logistics.module-bindings.json. Correct THERE
//     (that overlay is not a derivation source for the bridge roster), wrong HERE: it
//     would make the playbook-only `capability_validator` itself bridge-eligible.
//
// Re-arming rule (inherited from #4494): these bindings come back only when a real
// engine lands through the module factory — never by reverting this commit.
export const V1_LOGISTICS_EBITDA_MODULE_BINDING_OVERLAY = {
  overlayId: 'lever-module-bindings:v1-logistics-ebitda',
  // 1.7 → 1.9 (2026-08-05, #4449): governed binding-data change, five bindings removed.
  // 1.8 is already taken by the file overlay, whose content differs; skipped to keep the
  // two version lines from colliding on a number that means different things.
  version: 'lever-module-bindings:v1-logistics-ebitda@1.9',
  provenance:
    'docs/html/2026-05-25/v1-logistics-lever-taxonomy.html#encode',
  approvedBy: 'orchestrator-cto',
  bindings: [
    {
      bindingId: 'binding:carrier-renegotiation:v1',
      leverFamilyId: 'carrier_renegotiation',
      supportedQuestionFamilies: [
        'increase_ebitda',
        'reduce_transportation_cost',
        'identify_savings_levers',
      ],
      supportedModuleIds: ['carrier-transportation-cost'],
      requiredEvidence: [
        {
          evidenceId: 'carrier_rate_card',
          description: 'Governed carrier rate card and contract terms.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'carrierId',
        },
        {
          evidenceId: 'shipment_lane_spend',
          description: 'Governed TMS lane spend and shipment volume evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'laneId',
        },
        {
          evidenceId: 'carrier_contract_terms',
          description:
            'Governed carrier contract terms required to prove contractual savings.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'carrierId',
        },
      ],
      supportedToolPurposes: ['read_state'],
      outputMetrics: [
        {
          metricKey: 'carrier_cost_opportunity_usd',
          label: 'Carrier cost opportunity',
          unit: 'USD',
          definition:
            'Deterministic carrier cost opportunity once governed TMS and contract evidence are bound.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'carrier-transportation-cost-proof-v1',
          description:
            'Carrier proof policy must cite governed TMS spend and carrier contract evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:delayering:v1',
      leverFamilyId: 'delayering',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['org-analysis-optimization'],
      requiredEvidence: [
        {
          evidenceId: 'org_state_as_is',
          description: 'Governed granular org structure with reporting lines.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'span_layer_metrics',
          description:
            'Governed span, layer, depth, and bottleneck metrics derived from the org snapshot.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'position_cost_roster',
          description: 'Governed fully loaded cost roster by position.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'people_tenure',
          description:
            'Governed person or position tenure evidence for workforce policy and severance checks.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'personId',
        },
        {
          evidenceId: 'severance_policy',
          description:
            'Governed severance policy by level and jurisdiction.',
          sourceClass: 'manual-admin',
          requiredFreshness: 'quarterly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'policyId',
        },
        {
          evidenceId: 'initiative_capacity_binding',
          description:
            'Governed binding between positions, teams, initiatives, and capacity commitments for project-impact checks.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
      ],
      supportedToolPurposes: ['read_state'],
      outputMetrics: [
        {
          metricKey: 'delayering_net_ebitda_opportunity_usd',
          label: 'Delayering net EBITDA opportunity',
          unit: 'USD',
          definition:
            'Deterministic delayering opportunity once governed org, cost, and severance evidence are bound.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'delayering-ebitda-proof-v1',
          description:
            'Delayering proof policy must cite governed org structure, position cost, tenure, severance, and initiative-capacity evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
      contributionContract: {
        // A generic org number that is not a receipted sub-lever contribution must
        // never be counted (anti-fabrication guard, previously hardcoded in the
        // composite aggregator).
        rejectedGenericContributionClaimIdPatterns: ['org_ebitda_contribution_usd'],
        // The receipted org-cost contribution claim ids the roll-up accepts.
        acceptedContributionClaimIdPatterns: [
          'delayering_net_ebitda_opportunity',
          'org_cost_sub_lever_net_run_rate_contribution_usd',
        ],
        // Vacuous-basis guard: the non-workforce org-cost initiative pool is
        // NOT an internal-labor dollar claim. It must resolve overlap posture
        // against the `org_cost` registry family (every pair 'overlapping' —
        // exclusion receipt required), never inherit delayering's labor-basis
        // disjoint declarations (e.g. vs sga_spend).
        contributionOverlapFamilyOverrides: [
          {
            claimIdPattern: 'org_cost_sub_lever_net_run_rate_contribution_usd',
            overlapFamilyId: 'org_cost',
          },
        ],
      },
    },
    {
      bindingId: 'binding:pricing-uplift:v1',
      leverFamilyId: 'pricing_uplift',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['pricing-realization'],
      requiredEvidence: [
        {
          evidenceId: 'approved_pricing_realization_snapshot',
          description:
            'Governed customer sell-rate, leakage, retention or elasticity, and flat-revenue evidence.',
          // Matches the pricing-realization manifest requiredState sourceClass:
          // the approved activation is a modeled snapshot, and the routing-gate
          // readiness record carries the manifest-declared class.
          sourceClass: 'modeled',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'pricingScopeId',
        },
        {
          evidenceId: 'approved_pricing_execution_cost',
          description:
            'Governed execution-cost evidence required before pricing can claim operating-income contribution.',
          sourceClass: 'modeled',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'pricingScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'pricing_operating_income_contribution_usd',
          label: 'Pricing operating-income contribution',
          unit: 'USD',
          definition:
            'Formula-executed pricing realization contribution net of governed execution cost.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'pricing-realization-proof-v1',
          description:
            'Pricing proof policy must cite approved sell-rate, retention or elasticity, flat-revenue, execution-cost, and de-confliction evidence excluding carrier buy-rate.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:discount-leakage:v1',
      leverFamilyId: 'discount_leakage',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['pricing-realization'],
      requiredEvidence: [
        {
          evidenceId: 'approved_pricing_realization_snapshot',
          description:
            'Governed quote-to-invoice leakage and customer sell-rate realization evidence.',
          // Matches the pricing-realization manifest requiredState sourceClass:
          // the approved activation is a modeled snapshot, and the routing-gate
          // readiness record carries the manifest-declared class (same alignment
          // #3296 made for binding:pricing-uplift:v1 on the same two evidence ids).
          sourceClass: 'modeled',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'pricingScopeId',
        },
        {
          evidenceId: 'approved_pricing_execution_cost',
          description:
            'Governed execution-cost evidence required before discount leakage can claim operating-income contribution.',
          sourceClass: 'modeled',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'pricingScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'pricing_operating_income_contribution_usd',
          label: 'Pricing operating-income contribution',
          unit: 'USD',
          definition:
            'Formula-executed pricing realization contribution net of governed execution cost.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'pricing-realization-proof-v1',
          description:
            'Discount leakage proof policy must cite approved quote, invoice, leakage, execution-cost, and de-confliction evidence excluding carrier buy-rate.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:span-of-control-rightsizing:v1',
      leverFamilyId: 'span_of_control_rightsizing',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['org-analysis-optimization'],
      requiredEvidence: [
        {
          evidenceId: 'org_state_as_is',
          description: 'Governed granular org structure with reporting lines.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'span_layer_metrics',
          description:
            'Governed span, layer, depth, and bottleneck metrics derived from the org snapshot.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'position_cost_roster',
          description: 'Governed fully loaded cost roster by position.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'initiative_capacity_binding',
          description:
            'Governed binding between positions, teams, initiatives, and capacity commitments for project-impact checks.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
      ],
      supportedToolPurposes: ['read_state'],
      outputMetrics: [
        {
          metricKey: 'span_of_control_ebitda_contribution_usd',
          label: 'Span-of-control EBITDA contribution',
          unit: 'USD',
          definition:
            'Deterministic span-of-control contribution once governed org, span-layer, position-cost, and initiative-capacity evidence are bound and overlap with delayering is de-conflicted.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'span-of-control-ebitda-proof-v1',
          description:
            'Span-of-control proof policy must cite governed org structure, span-layer, position cost, initiative-capacity evidence, and any overlap adjustment against delayering.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
      contributionContract: {
        rejectedGenericContributionClaimIdPatterns: ['org_ebitda_contribution_usd'],
        acceptedContributionClaimIdPatterns: [
          'span_of_control_ebitda_contribution_usd',
          'org_cost_sub_lever_net_run_rate_contribution_usd',
        ],
      },
    },
    {
      bindingId: 'binding:workforce-reduction:v1',
      leverFamilyId: 'workforce_reduction',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['workforce-reduction'],
      requiredEvidence: [
        {
          evidenceId: 'org_state_as_is',
          description: 'Governed granular org structure with reporting lines.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'position_cost_roster',
          description: 'Governed fully loaded cost roster by position.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'people_tenure',
          description:
            'Governed person or position tenure evidence for workforce policy and severance checks.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'personId',
        },
        {
          evidenceId: 'severance_policy',
          description: 'Governed severance policy by level and jurisdiction.',
          sourceClass: 'manual-admin',
          requiredFreshness: 'quarterly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'policyId',
        },
        {
          evidenceId: 'initiative_capacity_binding',
          description:
            'Governed binding between positions, teams, initiatives, and capacity commitments for project-impact checks.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'weekly',
          requiredAsOfWindow: 'P7D',
          joinKey: 'positionId',
        },
        {
          evidenceId: 'workforce_legal_gate',
          description:
            'Server-trusted workforce scenario opt-in and legal gate before any workforce reduction contribution can enter EBITDA roll-up.',
          sourceClass: 'manual-admin',
          requiredFreshness: 'daily',
          requiredAsOfWindow: 'P1D',
          joinKey: 'scenarioId',
        },
      ],
      supportedToolPurposes: ['run_scenarios'],
      outputMetrics: [
        {
          metricKey: 'workforce_reduction_net_ebitda_opportunity_usd',
          label: 'Workforce reduction net EBITDA opportunity',
          unit: 'USD',
          definition:
            'Deterministic workforce reduction net EBITDA opportunity after one-time severance and overlap with other org-cost levers are de-conflicted.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'workforce-reduction-net-ebitda-proof-v1',
          description:
            'Workforce reduction proof policy must cite governed org cost, severance, server-trusted opt-in, legal gate, and any overlap adjustments against delayering or span-of-control.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
      contributionContract: {
        acceptedContributionClaimIdPatterns: [
          'workforce_reduction_net_ebitda_opportunity_usd',
        ],
        acceptedExactContributionClaimIds: [
          'workforce_reduction_net_ebitda_opportunity_usd',
        ],
        rejectedGenericContributionClaimIdPatterns: [
          'workforce_reduction_run_rate_savings_usd',
        ],
      },
    },
    {
      bindingId: 'binding:productivity-lean-ai:v1',
      leverFamilyId: 'productivity_lean_ai',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['productivity-lean-ai'],
      requiredEvidence: [
        {
          evidenceId: 'approved_productivity_realization_snapshot',
          description:
            'Governed throughput, automation-eligible cost, release-factor, and AI-investment evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'productivityScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'productivity_operating_income_contribution_usd',
          label: 'Productivity operating-income contribution',
          unit: 'USD',
          definition:
            'Formula-executed Lean + AI cost-release contribution on automation-eligible operating cost, net of governed AI investment.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'productivity-lean-ai-proof-v1',
          description:
            'Productivity proof policy must cite approved throughput, automation-eligible cost, release-factor, AI-investment, and labor-cluster shared-scope evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:segment-margin-cleanup:v1',
      leverFamilyId: 'segment_margin_cleanup',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['segment-margin-cleanup'],
      requiredEvidence: [
        {
          evidenceId: 'approved_segment_margin_snapshot',
          description:
            'Governed segment revenue, margin-floor, and cleanup-share evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'segmentId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'segment_cleanup_operating_income_contribution_usd',
          label: 'Segment cleanup operating-income contribution',
          unit: 'USD',
          definition:
            'Formula-executed segment margin cleanup contribution; an allocation lens that stays non-additive until exclusion receipts tie it to named underlying actions.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'segment-margin-cleanup-proof-v1',
          description:
            'Segment cleanup proof policy must cite approved segment revenue, floor-breach, cleanup-share, and exclusion-receipt evidence against underlying lever actions.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:sga-spend-aggregate:v1',
      leverFamilyId: 'sga_spend',
      supportedQuestionFamilies: ['increase_ebitda'],
      supportedModuleIds: ['sga-spend'],
      requiredEvidence: [
        {
          evidenceId: 'certified_sga_lever_metrics',
          description:
            'Governed formula-backed SG&A member metrics for the currently certified cost-pool set.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'costPoolId',
        },
        {
          evidenceId: 'finance_sga_line_reconciliation',
          description:
            'Governed finance reconciliation proving the certified SG&A member set belongs to the same operating-income line and reporting cut.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'reportingCut',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'sga_spend_total_opportunity_usd',
          label: 'Certified SG&A operating-income contribution',
          unit: 'USD',
          definition:
            'Fixed-key aggregate of the currently certified, formula-backed SG&A cost-pool opportunities; replaces its leaf claims in cross-module composition.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'sga-spend-aggregate-proof-v1',
          description:
            'The aggregate must carry the fixed numeric claim id, attributed costPoolIds members, approved formula receipts, and finance-line reconciliation.',
        },
      ],
      permissions: [
        { permission: 'decision_agent:read', required: true },
        { permission: 'finance:read', required: true },
      ],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
      contributionContract: {
        acceptedContributionClaimIdPatterns: [
          'sga_spend_total_opportunity_usd',
        ],
        acceptedExactContributionClaimIds: [
          'sga_spend_total_opportunity_usd',
        ],
      },
    },
    {
      bindingId: 'binding:travel-expense:v1',
      leverFamilyId: 'travel_and_expense',
      supportedQuestionFamilies: ['identify_savings_levers'],
      supportedModuleIds: ['sga-spend'],
      requiredEvidence: [
        {
          evidenceId: 'approved_tne_snapshot',
          description:
            'Governed total travel-and-expense spend, policy-exception spend, approved leakage, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'tneScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'tne_operating_income_contribution_usd',
          label: 'Travel & expense operating-income contribution',
          unit: 'USD',
          definition:
            'Formula-executed travel-and-expense leakage recovery on non-labor corporate SG&A spend, net of program cost; disjoint from labor, pricing, and purchased-transportation programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'travel-expense-proof-v1',
          description:
            'Travel-and-expense proof policy must cite approved total spend, policy-exception, leakage, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:indirect-procurement:v1',
      leverFamilyId: 'indirect_procurement',
      supportedQuestionFamilies: ['identify_savings_levers'],
      supportedModuleIds: ['sga-spend'],
      requiredEvidence: [
        {
          evidenceId: 'approved_indirect_procurement_snapshot',
          description:
            'Governed total non-transport indirect spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'indirectScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'indirect_procurement_operating_income_contribution_usd',
          label: 'Indirect procurement operating-income contribution',
          unit: 'USD',
          definition:
            'Formula-executed non-transport indirect vendor-consolidation savings net of program cost; disjoint from labor, pricing, purchased-transportation, and travel-and-expense programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'indirect-procurement-proof-v1',
          description:
            'Indirect procurement proof policy must cite approved total indirect spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:professional-fees:v1',
      leverFamilyId: 'professional_fees',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['professional-fees'],
      requiredEvidence: [
        {
          evidenceId: 'approved_professional_fees_snapshot',
          description: 'Governed total external fee spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'feeScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'professional_fees_operating_income_contribution_usd',
          label: 'Professional fees operating-income contribution',
          unit: 'USD',
          definition: 'Formula-executed external legal, consulting, audit, and advisory fee rationalization net of program cost; disjoint from labor, pricing, transport, and other SG&A spend programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'professional-fees-proof-v1',
          description: 'Professional fees proof policy must cite approved total fee spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:tech-software:v1',
      leverFamilyId: 'tech_software',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['tech-software'],
      requiredEvidence: [
        {
          evidenceId: 'approved_tech_software_snapshot',
          description: 'Governed total software spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'softwareScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'tech_software_operating_income_contribution_usd',
          label: 'Technology and software operating-income contribution',
          unit: 'USD',
          definition: 'Formula-executed SaaS, license, and infrastructure rationalization net of program cost; disjoint from labor, pricing, transport, and other SG&A spend programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'tech-software-proof-v1',
          description: 'Technology and software proof policy must cite approved total software spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:real-estate-footprint:v1',
      leverFamilyId: 'real_estate_footprint',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['real-estate-footprint'],
      requiredEvidence: [
        {
          evidenceId: 'approved_real_estate_footprint_snapshot',
          description: 'Governed total facility spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'facilityScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'real_estate_footprint_operating_income_contribution_usd',
          label: 'Real estate and facilities operating-income contribution',
          unit: 'USD',
          definition: 'Formula-executed real estate and facility footprint rationalization net of program cost; disjoint from labor, pricing, transport, and other SG&A spend programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'real-estate-footprint-proof-v1',
          description: 'Real estate and facilities proof policy must cite approved total facility spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:shared-services:v1',
      leverFamilyId: 'shared_services',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['shared-services'],
      requiredEvidence: [
        {
          evidenceId: 'approved_shared_services_snapshot',
          description: 'Governed total shared-services spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'functionScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'shared_services_operating_income_contribution_usd',
          label: 'Shared services operating-income contribution',
          unit: 'USD',
          definition: 'Formula-executed back-office shared-function consolidation net of program cost; disjoint from labor, pricing, transport, and other SG&A spend programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'shared-services-proof-v1',
          description: 'Shared services proof policy must cite approved total shared-services spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    {
      bindingId: 'binding:marketing-spend:v1',
      leverFamilyId: 'marketing_spend',
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'],
      supportedModuleIds: ['marketing-spend'],
      requiredEvidence: [
        {
          evidenceId: 'approved_marketing_spend_snapshot',
          description: 'Governed total marketing spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.',
          sourceClass: 'connector-derived',
          requiredFreshness: 'monthly',
          requiredAsOfWindow: 'P90D',
          joinKey: 'marketingScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'],
      outputMetrics: [
        {
          metricKey: 'marketing_spend_operating_income_contribution_usd',
          label: 'Marketing and demand-generation operating-income contribution',
          unit: 'USD',
          definition: 'Formula-executed marketing and demand-generation consolidation net of program cost; disjoint from labor, pricing, transport, and other SG&A spend programs.',
        },
      ],
      proofRequirements: [
        {
          proofKey: 'marketing-spend-proof-v1',
          description: 'Marketing spend proof policy must cite approved total marketing spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.',
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }],
      activationPolicy: 'approval_required',
      moduleCatalogVersion: 'decision-agent-module-catalog-v1',
    },
    ...(
      [
        ['network-optimization', 'network_optimization', 'Network design', 'redesignable lane', 'lane, route, and facility'],
        ['capacity-utilization', 'capacity_utilization', 'Capacity utilization', 'backhaul-addressable transport', 'empty-mile, asset-utilization, and load-factor'],
        // mode-mix-shift removed 2026-08-05 (#4449): module retired by #4494, no engine.
        // The `mode_mix_shift` lever family stays in the transportation overlay and now
        // resolves to `module_missing`.
        ['fuel-efficiency', 'fuel_efficiency', 'Fuel and accessorial', 'fuel and accessorial', 'fuel surcharge, accessorial, and carrier invoice-line'],
      ] as const
    ).map(([moduleId, snake, label, spendNoun, evidenceNoun]) => ({
      bindingId: `binding:${moduleId}:v1`,
      leverFamilyId: snake,
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'] as const,
      supportedModuleIds: [moduleId] as const,
      requiredEvidence: [
        {
          evidenceId: `approved_${snake}_snapshot`,
          description: `Governed ${spendNoun} spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence (${evidenceNoun} data).`,
          sourceClass: 'connector-derived' as const,
          requiredFreshness: 'monthly' as const,
          requiredAsOfWindow: 'P90D' as const,
          joinKey: 'laneScopeId' as const,
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'] as const,
      outputMetrics: [
        {
          metricKey: `${snake}_operating_income_contribution_usd`,
          label: `${label} operating-income contribution`,
          unit: 'USD' as const,
          definition: `Formula-executed ${spendNoun} savings net of program cost. Buy-side purchased-transportation lever: overlaps carrier renegotiation and the other transportation levers, so it is summed into the bridge only with a de-confliction receipt.`,
        },
      ],
      proofRequirements: [
        {
          proofKey: `${moduleId}-proof-v1`,
          description: `${label} proof policy must cite approved total ${spendNoun} spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.`,
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }] as const,
      activationPolicy: 'approval_required' as const,
      moduleCatalogVersion: 'decision-agent-module-catalog-v1' as const,
    })),
    ...(
      [
        ['outside-contractors', 'outside_contractors', 'Outside contractors', 'contingent-labor and outsourced-services', 'distinct SG&A vendor category, disjoint from labor, pricing, transport, and the other SG&A programs', 'functionScopeId'],
        ['labor-arbitrage', 'labor_arbitrage', 'Labor arbitrage', 'relocatable back-office labor', 'reduces the same internal labor cost pools as the org and workforce levers, so it overlaps them and is summed only with a de-confliction receipt', 'roleScopeId'],
        // revenue-mix-shift and growth-cross-sell removed 2026-08-05 (#4449): modules
        // retired by #4494, no engine. Both lever families stay in the core overlay and
        // now resolve to `module_missing`.
        ['churn-reduction', 'churn_reduction', 'Churn reduction', 'at-risk customer revenue', 'acts on the same customer revenue line as the pricing lever, so it overlaps the revenue side and is summed only with a de-confliction receipt', 'customerScopeId'],
        // working-capital and capex-discipline removed 2026-08-05 (#4449): modules retired
        // by #4494, no engine. Both were already excluded from the operating-income bridge
        // as cash-track levers, so their removal changes no bridge count — it removes the
        // false claim that an engine stood behind them at all.
      ] as const
    ).map(([moduleId, snake, label, spendNoun, disjointness, joinKey]) => ({
      bindingId: `binding:${moduleId}:v1`,
      leverFamilyId: snake,
      supportedQuestionFamilies: ['increase_ebitda', 'identify_savings_levers'] as const,
      supportedModuleIds: [moduleId] as const,
      requiredEvidence: [
        {
          evidenceId: `approved_${snake}_snapshot`,
          description: `Governed ${spendNoun} spend, addressable spend, approved savings rate, recovery-factor, and program-cost evidence.`,
          sourceClass: 'connector-derived' as const,
          requiredFreshness: 'monthly' as const,
          requiredAsOfWindow: 'P90D' as const,
          joinKey: joinKey as 'functionScopeId',
        },
      ],
      supportedToolPurposes: ['evaluate_monitoring'] as const,
      outputMetrics: [
        {
          metricKey: `${snake}_operating_income_contribution_usd`,
          label: `${label} operating-income contribution`,
          unit: 'USD' as const,
          definition: `Formula-executed ${spendNoun} savings net of program cost; ${disjointness}.`,
        },
      ],
      proofRequirements: [
        {
          proofKey: `${moduleId}-proof-v1`,
          description: `${label} proof policy must cite approved total ${spendNoun} spend, addressable spend, savings-rate, recovery-factor, and program-cost evidence.`,
        },
      ],
      permissions: [{ permission: 'decision_agent:read', required: true }] as const,
      activationPolicy: 'approval_required' as const,
      moduleCatalogVersion: 'decision-agent-module-catalog-v1' as const,
    })),
  ],
} as const satisfies ObjectiveModuleBindingOverlay;

export function listV1LogisticsEbitdaModuleBindings(): LeverModuleBinding[] {
  return [...V1_LOGISTICS_EBITDA_MODULE_BINDING_OVERLAY.bindings];
}

function findLeverFamily(
  overlay: ObjectiveTaxonomyOverlay,
  leverFamilyId: string,
): LeverFamily {
  const leverFamily = overlay.leverFamilies.find(
    (candidate) => candidate.id === leverFamilyId,
  );
  if (!leverFamily) {
    throw new Error(
      `Lever family ${leverFamilyId} is missing from ${overlay.version}.`,
    );
  }
  return leverFamily;
}

export const OBJECTIVE_DECOMPOSER_ASSERTION_CATALOG = [
  {
    assertionId: 'objective_parse',
    description: 'The objective family, metric, direction, target, scope, and timeframe resolve.',
  },
  {
    assertionId: 'value_gap_anchor_code_owned',
    description: 'Baseline and target dollar math live in ObjectiveFrame, not LeverPlan.',
  },
  {
    assertionId: 'taxonomy_id_valid',
    description: 'All model-selected leverFamilyId values exist in the resolved taxonomy stack.',
  },
  {
    assertionId: 'no_model_dollars',
    description: 'The LLM-owned LeverPlan contains no baseline, target, impact, or savings values.',
  },
  {
    assertionId: 'no_model_module_ids',
    description: 'The LLM-owned LeverPlan contains no module IDs, tool names, or target engines.',
  },
  {
    assertionId: 'gap_status_correct',
    description: 'Missing evidence and missing modules are separated deterministically.',
  },
  {
    assertionId: 'wrong_tool_prevention',
    description: 'A model-proposed module/tool is rejected because code owns mapping.',
  },
  {
    assertionId: 'proof_linked_answer',
    description: 'Any final answer number must trace to numeric bindings and proof refs.',
  },
  {
    assertionId: 'three_bucket_partition',
    description: 'Every applicable lever lands in provable, unlock, not applicable, or candidate review.',
  },
] as const satisfies readonly Readonly<{
  assertionId: ObjectiveDecomposerEvalAssertionId;
  description: string;
}>[];

export const EBITDA_DECOMPOSER_EVAL_TRACES = [
  {
    evalId: 'E1',
    title: 'Happy path EBITDA increase with governed baseline',
    question: 'How do we increase EBITDA by 10%?',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: ['carrier_renegotiation', 'delayering', 'pricing_uplift'],
    expectedStatuses: {
      carrier_renegotiation: 'needs_evidence',
      delayering: 'needs_evidence',
      pricing_uplift: 'needs_evidence',
    },
    assertions: [
      'objective_parse',
      'value_gap_anchor_code_owned',
      'taxonomy_id_valid',
      'no_model_dollars',
      'no_model_module_ids',
      'three_bucket_partition',
    ],
  },
  {
    evalId: 'E2',
    title: 'Missing baseline blocks value-gap sizing',
    question: 'Increase EBITDA by 10% without a finance baseline.',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: [],
    expectedStatuses: {},
    assertions: ['objective_parse', 'value_gap_anchor_code_owned'],
    expectedBlockReason: 'missing_governed_baseline',
  },
  {
    evalId: 'E3',
    title: 'Wrong-tool prevention blocks model invented modules',
    question: 'Use whatever module can find EBITDA savings.',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: ['carrier_renegotiation'],
    expectedStatuses: { carrier_renegotiation: 'needs_evidence' },
    assertions: ['no_model_module_ids', 'wrong_tool_prevention'],
  },
  {
    evalId: 'E4',
    title: 'No fake math when evidence is missing',
    question: 'Show carrier savings for EBITDA.',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: ['carrier_renegotiation'],
    expectedStatuses: { carrier_renegotiation: 'needs_evidence' },
    assertions: ['no_model_dollars', 'gap_status_correct'],
  },
  {
    evalId: 'E5',
    title: 'Differentiate module missing from evidence missing',
    question: 'What EBITDA levers can we pull in logistics?',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: ['carrier_renegotiation', 'network_optimization', 'warehouse_productivity'],
    expectedStatuses: {
      carrier_renegotiation: 'needs_evidence',
      network_optimization: 'module_missing',
      warehouse_productivity: 'module_missing',
    },
    assertions: ['gap_status_correct', 'three_bucket_partition'],
  },
  {
    evalId: 'E6',
    title: 'Off-taxonomy proposal becomes candidate for review',
    question: 'Invent a new EBITDA improvement idea.',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: ['moonshot_ai_dispatch'],
    expectedStatuses: { moonshot_ai_dispatch: 'candidate_for_review' },
    assertions: ['three_bucket_partition'],
  },
  {
    evalId: 'E7',
    title: 'Out-of-scope question does not call modules',
    question: 'Write a poem about logistics.',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'out_of_scope',
    expectedLeverFamilies: [],
    expectedStatuses: {},
    assertions: ['wrong_tool_prevention'],
    expectedBlockReason: 'out_of_scope',
  },
  {
    evalId: 'E8',
    title: 'LLM-off parity blocks safely',
    question: 'How do we increase EBITDA by 10%?',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: [],
    expectedStatuses: {},
    assertions: ['value_gap_anchor_code_owned', 'no_model_dollars'],
    expectedBlockReason: 'llm_unavailable',
  },
  {
    evalId: 'E9',
    title: 'Completeness and three-bucket partition',
    question: 'List the full EBITDA lever universe and what Helix can prove now.',
    fixtureId: 'northwind_logistics_governed_fixture_v1',
    expectedQuestionFamily: 'increase_ebitda',
    expectedLeverFamilies: [
      'carrier_renegotiation',
      'network_optimization',
      'mode_mix_shift',
      'capacity_utilization',
      'fuel_efficiency',
      'delayering',
      'travel_and_expense',
      'indirect_procurement',
      'professional_fees',
      'tech_software',
      'outside_contractors',
      'labor_arbitrage',
      'real_estate_footprint',
      'shared_services',
      'pricing_uplift',
      'discount_leakage',
      'revenue_mix_shift',
      'growth_cross_sell',
      'churn_reduction',
      'warehouse_productivity',
      'automation',
      'working_capital',
      'capex_discipline',
    ],
    expectedStatuses: {
      carrier_renegotiation: 'needs_evidence',
      network_optimization: 'module_missing',
      mode_mix_shift: 'module_missing',
      capacity_utilization: 'module_missing',
      fuel_efficiency: 'module_missing',
      delayering: 'needs_evidence',
      travel_and_expense: 'module_missing',
      indirect_procurement: 'module_missing',
      professional_fees: 'module_missing',
      tech_software: 'module_missing',
      outside_contractors: 'module_missing',
      labor_arbitrage: 'module_missing',
      real_estate_footprint: 'module_missing',
      shared_services: 'module_missing',
      pricing_uplift: 'needs_evidence',
      discount_leakage: 'needs_evidence',
      revenue_mix_shift: 'module_missing',
      growth_cross_sell: 'module_missing',
      churn_reduction: 'module_missing',
      warehouse_productivity: 'module_missing',
      automation: 'module_missing',
      working_capital: 'module_missing',
      capex_discipline: 'module_missing',
    },
    assertions: ['three_bucket_partition', 'proof_linked_answer', 'gap_status_correct'],
  },
] as const satisfies readonly ObjectiveDecomposerEvalTrace[];
